import { useCallback, useEffect, useMemo, useState } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import { exportToExcel } from "../utils/exportData";
import ExportButton from "../component/ExportButton";
import axios from "axios";
import { BASE_URL } from "../api";
import Loader from "../component/Loader";
import MessageToster from "../component/MessageToster";
import { getApiHeaders, formatDateDDMMYYYY } from "../utils/helpers";
import { useHandleError } from "../hooks/useHandleError";
import CommonTable from "../component/CommonTable";

export default function IbPendingList() {
    const handleError = useHandleError();
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [data, setData] = useState([]);

    const today = new Date().toISOString().split("T")[0];
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [searchTerm, setSearchTerm] = useState("");

    // Modal state for View Details & Approve / Reject action
    const [viewItem, setViewItem] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [actionType, setActionType] = useState(null); // 'Active' or 'Reject'
    const [modalAmount, setModalAmount] = useState("");
    const [rejectReason, setRejectReason] = useState("");

    // 1. Fetch IB Pending List
    const fetchPendingList = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${BASE_URL}/IB/ib_pending_list`, {
                headers: getApiHeaders()
            });

            if (res && res.data) {
                let resData = res.data.data !== undefined ? res.data.data : res.data;
                if (typeof resData === "string") {
                    try {
                        resData = JSON.parse(resData);
                    } catch {
                        // ignore
                    }
                }

                if (Array.isArray(resData)) {
                    setData(resData);
                } else if (resData && typeof resData === "object") {
                    const possibleList =
                        resData.pending_list ||
                        resData.ib_pending_list ||
                        resData.list ||
                        resData.records ||
                        resData.data;

                    if (Array.isArray(possibleList)) {
                        setData(possibleList);
                    } else {
                        setData([]);
                    }
                } else {
                    setData([]);
                }

                if (
                    res.data.code &&
                    res.data.code !== 200 &&
                    res.data.code !== "200" &&
                    res.data.status !== "success" &&
                    res.data.status !== true
                ) {
                    if (res.data.message) {
                        MessageToster("error", res.data.message);
                    }
                }
            } else {
                setData([]);
            }
        } catch (error) {
            setData([]);
            handleError(error);
        } finally {
            setLoading(false);
        }
    }, [handleError]);

    useEffect(() => {
        fetchPendingList();
    }, [fetchPendingList]);

    // 2. Open Action Confirmation Modal
    const handleOpenActionModal = (item, type) => {
        setSelectedItem(item);
        setActionType(type); // "Active" for Approve, "Reject" for Reject
        setRejectReason("");
        if (type === "Active") {
            const initialCommission = item.commision_per !== undefined && item.commision_per !== null
                ? item.commision_per
                : "";
            setModalAmount(String(initialCommission));
        } else {
            setModalAmount("0");
        }
    };

    const handleCloseModal = () => {
        setSelectedItem(null);
        setActionType(null);
        setModalAmount("");
        setRejectReason("");
    };

    // 3. Submit Approve or Reject API
    const handleConfirmAction = async () => {
        if (!selectedItem || !actionType) return;

        const ibId = Number(selectedItem.id ?? selectedItem.ib_id ?? selectedItem._id ?? 0);
        const mid = Number(selectedItem.mid ?? 0);
        const statusValue = actionType === "Active" ? "Active" : "Reject";

        let payload;
        if (actionType === "Active") {
            const commNum = Number(modalAmount);
            if (modalAmount === "" || isNaN(commNum) || commNum < 0) {
                MessageToster("error", "Please enter a valid commission amount.");
                return;
            }
            payload = {
                ib_id: ibId,
                mid: mid,
                status: "Active",
                amount: commNum,
                reject_reason: ""
            };
        } else {
            if (!rejectReason.trim()) {
                MessageToster("error", "Please enter rejection reason.");
                return;
            }
            payload = {
                ib_id: ibId,
                mid: mid,
                status: "Reject",
                amount: 0,
                reject_reason: rejectReason.trim()
            };
        }

        setActionLoading(true);
        try {
            const res = await axios.post(`${BASE_URL}/IB/approve_reject_ib`, payload, {
                headers: getApiHeaders()
            });

            const resData = res?.data;
            const isSuccess =
                Boolean(resData) &&
                (resData.code === 200 || resData.code === "200") &&
                (resData.status === "success" || resData.status === true) &&
                resData.status !== "fail" &&
                resData.status !== "error";

            if (isSuccess) {
                const actionLabel = actionType === "Active" ? "approved" : "rejected";
                const successMsg =
                    typeof resData?.message === "string"
                        ? resData.message
                        : `IB request ${actionLabel} successfully!`;
                MessageToster("success", successMsg);
                handleCloseModal();
                fetchPendingList();
            } else {
                const failMsg =
                    typeof resData?.message === "string"
                        ? resData.message
                        : "Operation failed. Please try again.";
                MessageToster("error", failMsg);
            }
        } catch (error) {
            handleError(error);
        } finally {
            setActionLoading(false);
        }
    };

    // 4. Client-side Search and Date Filtering
    const filteredData = useMemo(() => {
        if (!Array.isArray(data)) return [];

        return data.filter((item) => {
            const rawDate = item.ondate || item.created_at || item.create_date || item.date || item.time;
            const itemDate = rawDate ? new Date(rawDate).toISOString().split("T")[0] : "";

            if (fromDate && itemDate && itemDate < fromDate) return false;
            if (toDate && itemDate && itemDate > toDate) return false;

            if (searchTerm.trim()) {
                const q = searchTerm.toLowerCase();
                const ibId = String(item.ib_id || item.id || "").toLowerCase();
                const username = String(item.username || "").toLowerCase();
                const email = String(item.email || item.ib_email || item.ib || "").toLowerCase();
                const name = String(item.name || item.full_name || "").toLowerCase();
                const status = String(item.status || "").toLowerCase();

                const matches =
                    ibId.includes(q) ||
                    username.includes(q) ||
                    email.includes(q) ||
                    name.includes(q) ||
                    status.includes(q);

                if (!matches) return false;
            }

            return true;
        });
    }, [data, fromDate, toDate, searchTerm]);

    const handleClearFilters = () => {
        setFromDate("");
        setToDate("");
        setSearchTerm("");
    };

    const handleExport = () => {
        if (!filteredData || filteredData.length === 0) {
            MessageToster("error", "No records found to export.");
            return;
        }

        const exportRows = filteredData.map((row, index) => ({
            "No.": index + 1,
            "Date": row.ondate || row.created_at || row.date ? formatDateDDMMYYYY(row.ondate || row.created_at || row.date) : "-",
            "IB ID": row.id || row.ib_id || "-",
            "Full Name": row.full_name || row.name || row.username || "-",
            "Email": row.email || row.ib_email || "-",
            "Company": row.companyname || "-",
            "Experience (Yrs)": row.yearofexp || "-",
            "Existing Clients": row.existclients ?? "-",
            "Avg Volume": row.avgvolume != null ? `$${Number(row.avgvolume).toLocaleString()}` : "-",
            "Parent Affiliate": row.parent_affiliate_code || "-",
            "Status": row.status || "Pending",
            "Reject Reason": row.reject_reason || "-"
        }));
        exportToExcel(exportRows, "IB_Pending_List_Report", "default");
    };

    // 5. Columns Configuration for CommonTable (Note: CommonTable already provides the auto-indexed 'No.' column)
    const columns = [
        {
            header: "Date",
            render: (row) => {
                const raw = row.ondate || row.created_at || row.create_date || row.date || row.time;
                return raw ? formatDateDDMMYYYY(raw) : "-";
            }
        },
        {
            header: "IB ID",
            render: (row) => {
                const id = row.id ?? row.ib_id ?? "-";
                return (
                    <span className="badge bg-secondary-subtle text-secondary font-monospace px-2 py-1 font-size-12">
                        #{id}
                    </span>
                );
            }
        },
        {
            header: "Applicant / User",
            render: (row) => {
                return (
                    <div>
                        <div className="fw-semibold text-dark font-size-13">
                            {row.full_name || row.username || row.name || "-"}
                        </div>
                        {row.email && (
                            <small className="text-muted font-size-11 font-monospace d-block">
                                {row.email}
                            </small>
                        )}
                        {row.parent_affiliate_code && (
                            <small className="text-secondary font-size-10">
                                Ref: {row.parent_affiliate_code}
                            </small>
                        )}
                    </div>
                );
            }
        },
        {
            header: "Company & Exp",
            render: (row) => {
                return (
                    <div>
                        <div className="fw-medium text-dark font-size-12">
                            {row.companyname ? (
                                <span className="badge bg-light text-dark border me-1">
                                    {row.companyname}
                                </span>
                            ) : (
                                "-"
                            )}
                        </div>
                        <small className="text-muted font-size-11">
                            Exp: {row.yearofexp ? `${row.yearofexp} yrs` : "-"} | Clients: {row.existclients ?? "-"}
                        </small>
                    </div>
                );
            }
        },
        {
            header: "Avg Volume",
            render: (row) => {
                const val = row.avgvolume ?? row.amount ?? row.cpa_amount;
                if (val == null || val === "") return "-";
                const num = Number(val);
                if (isNaN(num)) return String(val);
                return (
                    <span className="fw-bold text-success font-monospace font-size-13">
                        ${num.toLocaleString()}
                    </span>
                );
            }
        },
        {
            header: "Status",
            render: (row) => {
                const status = row.status || "Pending";
                const st = String(status).toLowerCase();
                let badgeClass = "bg-warning bg-opacity-10 text-warning";
                if (["active", "approved", "confirm", "confirmed"].includes(st)) {
                    badgeClass = "bg-success bg-opacity-10 text-success";
                } else if (["rejected", "reject"].includes(st)) {
                    badgeClass = "bg-danger bg-opacity-10 text-danger";
                }
                return (
                    <div>
                        <span className={`badge px-2 py-1 ${badgeClass}`}>
                            {status}
                        </span>
                        {row.reject_reason && (
                            <small className="text-danger d-block font-size-11 mt-1" title={row.reject_reason}>
                                Reason: {row.reject_reason}
                            </small>
                        )}
                    </div>
                );
            }
        },
        {
            header: "Action",
            render: (row) => {
                const st = String(row.status || "Pending").toLowerCase();
                const isPending = !["active", "approved", "rejected", "reject"].includes(st);

                return (
                    <div className="d-flex align-items-center gap-1">
                        <button
                            type="button"
                            className="btn btn-sm btn-primary d-flex align-items-center px-2 py-1 font-size-12"
                            onClick={() => setViewItem(row)}
                            title="View full details"
                        >
                            <i className="bx bx-show me-1" />
                            View
                        </button>
                        {isPending ? (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-success d-flex align-items-center px-2 py-1 font-size-12"
                                    onClick={() => handleOpenActionModal(row, "Active")}
                                    title="Approve this IB"
                                >
                                    <i className="bx bx-check me-1" />
                                    Approve
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger d-flex align-items-center px-2 py-1 font-size-12"
                                    onClick={() => handleOpenActionModal(row, "Reject")}
                                    title="Reject this IB"
                                >
                                    <i className="bx bx-x me-1" />
                                    Reject
                                </button>
                            </>
                        ) : (
                            <span className="text-muted font-size-12 ms-1">
                                Processed
                            </span>
                        )}
                    </div>
                );
            }
        }
    ];

    return (
        <div id="layout-wrapper">
            {loading && <Loader />}
            <Topbar />
            <Sidebar />
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        {/* Page Header */}
                        <div className="row">
                            <div className="col-12">
                                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                    <h2 className="fw-700 mb-sm-0">IB Pending List</h2>
                                </div>
                            </div>
                        </div>

                        {/* Filters & Actions Card */}
                        <div className="row">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="d-flex flex-wrap align-items-end gap-2 mb-4">
                                            <div>
                                                <label className="form-label mb-1 font-size-12">From Date</label>
                                                <input
                                                    type="date"
                                                    className="form-control form-control-sm"
                                                    value={fromDate}
                                                    max={toDate || today}
                                                    onChange={(e) => setFromDate(e.target.value)}
                                                />
                                            </div>

                                            <div>
                                                <label className="form-label mb-1 font-size-12">To Date</label>
                                                <input
                                                    type="date"
                                                    className="form-control form-control-sm"
                                                    value={toDate}
                                                    min={fromDate}
                                                    max={today}
                                                    onChange={(e) => setToDate(e.target.value)}
                                                />
                                            </div>

                                            <div className="flex-grow-1" style={{ minWidth: "200px" }}>
                                                <label className="form-label mb-1 font-size-12">Search</label>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    placeholder="Search username, email, ID..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                            </div>

                                            <div>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-secondary"
                                                    onClick={handleClearFilters}
                                                    title="Clear All Filters"
                                                >
                                                    Clear
                                                </button>
                                            </div>

                                            <div>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={fetchPendingList}
                                                    title="Refresh Table"
                                                >
                                                    <i className="bx bx-refresh me-1" />
                                                    Refresh
                                                </button>
                                            </div>

                                            <div className="ms-auto">
                                                <ExportButton
                                                    data={filteredData}
                                                    onExport={handleExport}
                                                    title="Export"
                                                />
                                            </div>
                                        </div>

                                        {/* Table */}
                                        <CommonTable
                                            columns={columns}
                                            data={filteredData}
                                            loading={loading}
                                            noDataMessage="No pending IB requests found"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />

            {/* View Details Modal */}
            {viewItem && (
                <div
                    className="modal fade show d-block"
                    tabIndex="-1"
                    style={{ backgroundColor: "rgba(0, 0, 0, 0.55)", zIndex: 1050 }}
                >
                    <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "560px" }}>
                        <div className="modal-content border-0 shadow-lg">
                            <div className="modal-header bg-light py-2 px-3">
                                <h6 className="modal-title fw-bold text-dark d-flex align-items-center mb-0">
                                    <i className="bx bx-user-pin text-primary me-2 font-size-18" />
                                    IB Application Details
                                </h6>
                                <button
                                    type="button"
                                    className="btn-close font-size-12"
                                    onClick={() => setViewItem(null)}
                                />
                            </div>

                            <div className="modal-body p-3">
                                {/* Section 1: Applicant Information */}
                                <h6 className="text-uppercase font-size-11 text-muted fw-bold mb-2 border-bottom pb-1">
                                    Applicant Information
                                </h6>
                                <div className="row g-2 mb-3">
                                    <div className="col-6">
                                        <div className="text-muted font-size-11">Full Name</div>
                                        <div className="fw-bold text-dark font-size-13">
                                            {viewItem.full_name || viewItem.username || viewItem.name || "-"}
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="text-muted font-size-11">Email</div>
                                        <div className="fw-semibold text-dark font-size-12 font-monospace">
                                            {viewItem.email || viewItem.ib_email || "-"}
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="text-muted font-size-11">Application Date</div>
                                        <div className="fw-medium text-dark font-size-12">
                                            {viewItem.ondate || viewItem.created_at || viewItem.date
                                                ? formatDateDDMMYYYY(viewItem.ondate || viewItem.created_at || viewItem.date)
                                                : "-"}
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="text-muted font-size-11">Status</div>
                                        <div>
                                            <span
                                                className={`badge px-2 py-1 font-size-11 ${["active", "approved"].includes(String(viewItem.status).toLowerCase())
                                                    ? "bg-success bg-opacity-10 text-success"
                                                    : ["reject", "rejected"].includes(String(viewItem.status).toLowerCase())
                                                        ? "bg-danger bg-opacity-10 text-danger"
                                                        : "bg-warning bg-opacity-10 text-warning"
                                                    }`}
                                            >
                                                {viewItem.status || "Pending"}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="text-muted font-size-11">IB Wallet ID</div>
                                        <div className="fw-semibold font-monospace text-secondary font-size-12">
                                            {viewItem.ibwalletid ?? "-"}
                                        </div>
                                    </div>
                                </div>

                                {/* Section 2: Business & Experience Profile */}
                                <h6 className="text-uppercase font-size-11 text-muted fw-bold mb-2 border-bottom pb-1">
                                    Business & Experience Profile
                                </h6>
                                <div className="row g-2 mb-3">
                                    <div className="col-6">
                                        <div className="text-muted font-size-11">Company Name</div>
                                        <div className="fw-bold text-dark font-size-12">
                                            {viewItem.companyname || "-"}
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="text-muted font-size-11">Experience</div>
                                        <div className="fw-semibold text-dark font-size-12">
                                            {viewItem.yearofexp ? `${viewItem.yearofexp} Years` : "-"}
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="text-muted font-size-11">Existing Clients</div>
                                        <div className="fw-bold text-dark font-size-12">
                                            {viewItem.existclients ?? "-"}
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="text-muted font-size-11">Avg Trading Volume</div>
                                        <div className="fw-bold text-success font-monospace font-size-13">
                                            {viewItem.avgvolume != null
                                                ? `$${Number(viewItem.avgvolume).toLocaleString()}`
                                                : "-"}
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="text-muted font-size-11">Commission Amount</div>
                                        <div className="fw-semibold text-dark font-size-12">
                                            {viewItem.commision_per != null ? (isNaN(Number(viewItem.commision_per)) ? viewItem.commision_per : `$${Number(viewItem.commision_per).toLocaleString()}`) : "-"}
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="text-muted font-size-11">Note</div>
                                        <div className="text-secondary font-size-12">
                                            {viewItem.note || "-"}
                                        </div>
                                    </div>
                                </div>

                                {/* Section 3: Affiliation Details */}
                                <h6 className="text-uppercase font-size-11 text-muted fw-bold mb-2 border-bottom pb-1">
                                    Affiliation Details
                                </h6>
                                <div className="row g-2">
                                    <div className="col-6">
                                        <div className="text-muted font-size-11">Parent Affiliate Code</div>
                                        <div className="fw-bold font-monospace text-primary font-size-12">
                                            {viewItem.parent_affiliate_code || "-"}
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="text-muted font-size-11">Approver Type</div>
                                        <div>
                                            <span className="badge bg-info-subtle text-info px-2 py-1 font-size-11">
                                                {viewItem.approver_type || "-"}
                                            </span>
                                        </div>
                                    </div>
                                    {viewItem.reject_reason && (
                                        <div className="col-12">
                                            <div className="text-danger font-size-11 fw-semibold">Reject Reason</div>
                                            <div className="alert alert-danger py-1 px-2 mt-1 font-size-12 mb-0">
                                                {viewItem.reject_reason}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="modal-footer bg-light py-2 px-3 d-flex justify-content-between">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-secondary"
                                    onClick={() => setViewItem(null)}
                                >
                                    Close
                                </button>
                                {!["active", "approved", "reject", "rejected"].includes(
                                    String(viewItem.status || "").toLowerCase()
                                ) && (
                                        <div className="d-flex gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger d-flex align-items-center px-2 py-1 font-size-12"
                                                onClick={() => {
                                                    const target = viewItem;
                                                    setViewItem(null);
                                                    handleOpenActionModal(target, "Reject");
                                                }}
                                            >
                                                <i className="bx bx-x me-1" />
                                                Reject
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-success d-flex align-items-center px-2 py-1 font-size-12"
                                                onClick={() => {
                                                    const target = viewItem;
                                                    setViewItem(null);
                                                    handleOpenActionModal(target, "Active");
                                                }}
                                            >
                                                <i className="bx bx-check me-1" />
                                                Approve
                                            </button>
                                        </div>
                                    )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Modal for Approve / Reject */}
            {selectedItem && (
                <div
                    className="modal fade show d-block"
                    tabIndex="-1"
                    style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1055 }}
                >
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg">
                            <div className="modal-header">
                                <h5 className="modal-title fw-bold">
                                    {actionType === "Active" ? (
                                        <span className="text-success">
                                            <i className="bx bx-check-circle me-1" />
                                            Approve IB Request
                                        </span>
                                    ) : (
                                        <span className="text-danger">
                                            <i className="bx bx-error-circle me-1" />
                                            Reject IB Request
                                        </span>
                                    )}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={handleCloseModal}
                                    disabled={actionLoading}
                                />
                            </div>

                            <div className="modal-body">
                                <p className="text-secondary font-size-13 mb-3">
                                    {actionType === "Active"
                                        ? "Are you sure you want to approve this IB request? This will activate their IB account."
                                        : "Are you sure you want to reject this IB request? This action will set the status to Reject."}
                                </p>

                                <div className="p-3 bg-light rounded mb-3">
                                    <div className="d-flex justify-content-between mb-2 font-size-13">
                                        <span className="text-muted">IB ID:</span>
                                        <span className="fw-semibold font-monospace">
                                            #{selectedItem.ib_id ?? selectedItem.id ?? "-"}
                                        </span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2 font-size-13">
                                        <span className="text-muted">Applicant Name:</span>
                                        <span className="fw-semibold">
                                            {selectedItem.full_name || selectedItem.username || selectedItem.name || "-"}
                                        </span>
                                    </div>
                                    {(selectedItem.email || selectedItem.ib_email) && (
                                        <div className="d-flex justify-content-between font-size-13">
                                            <span className="text-muted">Email:</span>
                                            <span className="text-dark">
                                                {selectedItem.email || selectedItem.ib_email}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Conditional Input: Commission Amount for Approve vs Reason for Reject */}
                                {actionType === "Active" ? (
                                    <div className="mb-3">
                                        <label className="form-label font-size-13 fw-semibold">
                                            Commission Amount <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">$</span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                className="form-control"
                                                placeholder="Enter commission amount"
                                                value={modalAmount}
                                                onChange={(e) => setModalAmount(e.target.value)}
                                                disabled={actionLoading}
                                                autoFocus
                                            />
                                        </div>
                                        <small className="text-muted font-size-11">
                                            Enter approved commission amount.
                                        </small>
                                    </div>
                                ) : (
                                    <div className="mb-3">
                                        <label className="form-label font-size-13 fw-semibold">
                                            Rejection Reason <span className="text-danger">*</span>
                                        </label>
                                        <textarea
                                            rows="3"
                                            className="form-control"
                                            placeholder="Enter reason for rejecting this IB request..."
                                            value={rejectReason}
                                            onChange={(e) => setRejectReason(e.target.value)}
                                            disabled={actionLoading}
                                            autoFocus
                                        />
                                        <small className="text-muted font-size-11">
                                            This reason will be passed as reject_reason in the API.
                                        </small>
                                    </div>
                                )}
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-secondary"
                                    onClick={handleCloseModal}
                                    disabled={actionLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-sm ${actionType === "Active" ? "btn-success" : "btn-danger"} d-flex align-items-center`}
                                    onClick={handleConfirmAction}
                                    disabled={actionLoading}
                                >
                                    {actionLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status" />
                                            Processing...
                                        </>
                                    ) : actionType === "Active" ? (
                                        <>
                                            <i className="bx bx-check me-1" />
                                            Confirm Approve
                                        </>
                                    ) : (
                                        <>
                                            <i className="bx bx-x me-1" />
                                            Confirm Reject
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
